import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Lock, Refresh, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { mfaTheme } from './them';
import BgImage from '../../Res/bg.jpg';

const SECRET_KEY = 'TyybitrdGUBJKBT7968#]#[##[LJBKJBUIG==';

interface MfaVerifyPageProps {
  onVerified?: () => void;
}

export const MfaVerifyPage: React.FC<MfaVerifyPageProps> = ({ onVerified }) => {
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [mfaMethod, setMfaMethod] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Charger la méthode MFA assignée depuis sessionStorage
    const assignedMethod = sessionStorage.getItem('assignedMfaMethod');
    if (assignedMethod) {
      setMfaMethod(assignedMethod);
    }
  }, []);

  // Envoyer automatiquement l'OTP au chargement pour Email/SMS
  useEffect(() => {
    if (mfaMethod && (mfaMethod === '2' || mfaMethod === '3')) {
      sendChallenge();
    }
  }, [mfaMethod]);

  const encrypt = (data: any): string => {
    const jsonString = JSON.stringify(data);
    return CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
  };

  const decrypt = (cipherText: string): any => {
    const bytes = CryptoJS.AES.decrypt(cipherText, SECRET_KEY);
    const jsonString = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(jsonString);
  };

  const sendChallenge = async () => {
    setSendingOtp(true);
    setMessage(null);

    try {
      const codUti = localStorage.getItem('codUti') || sessionStorage.getItem('codUti');
      if (!codUti) {
        setMessage({ type: 'error', text: 'Utilisateur non identifié' });
        setSendingOtp(false);
        return;
      }

      const request = {
        codUti,
        ipAddress: '127.0.0.1',
        userAgent: navigator.userAgent
      };

      const encryptedRequest = encrypt(request);

      const response = await axios.post('/UserMfa/Challenge', encryptedRequest, {
        headers: { 'Content-Type': 'application/json' }
      });

      const decryptedResponse = decrypt(response.data);
      
      if (decryptedResponse.StatutCode === 200) {
        setMessage({ type: 'success', text: 'Code envoyé avec succès' });
        // Démarrer le compte à rebours (5 minutes)
        setCountdown(300);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de l\'envoi du code.veillez contacter votre administrateur.' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'envoi du code.veillez contacter votre administrateur.' });
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const codUti = localStorage.getItem('codUti') || sessionStorage.getItem('codUti');
      if (!codUti) {
        setMessage({ type: 'error', text: 'Utilisateur non identifié' });
        setLoading(false);
        return;
      }

      const request = {
        codUti,
        otpCode,
        methodType: parseInt(mfaMethod || '0')
      };

      const encryptedRequest = encrypt(request);

      const response = await axios.post('/Authentification/verify-otp', encryptedRequest, {
        headers: { 'Content-Type': 'application/json' }
      });

      const decryptedResponse = decrypt(response.data);

      if (decryptedResponse.StatutCode === 200) {
        setMessage({ type: 'success', text: 'MFA vérifié avec succès' });
        
        // Mettre à jour le cookie avec mfa_verified=true
        if (decryptedResponse.Result?.jeton) {
          document.cookie = `AuthToken=${decryptedResponse.Result.jeton}; path=/; secure; samesite=strict`;
        }

        // Nettoyer sessionStorage
        sessionStorage.removeItem('assignedMfaMethod');
        sessionStorage.removeItem('mfaRequired');
        sessionStorage.removeItem('requiresSetup');

        // Rediriger vers la page d'accueil ou appeler le callback
        setTimeout(() => {
          if (onVerified) {
            onVerified();
          } else {
            window.location.replace('/');
          }
        }, 1000);
      } else {
        setMessage({ type: 'error', text: 'Code invalide.veillez contacter votre administrateur.' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erreur lors de la vérification.veillez contacter votre administrateur.' });
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMethodLabel = () => {
    switch (mfaMethod) {
      case '1': return 'Application d\'authentification (TOTP)';
      case '2': return 'Code par email';
      case '3': return 'Code par SMS';
      default: return 'MFA';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        backgroundImage: `url(${BgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(8,35,56, 0.88) 0%, rgba(0, 0, 0, 0.72) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Card className="login-card light" sx={{ maxWidth: 500, width: '100%', position: 'relative', zIndex: 1 }}>
        <CardContent sx={{ padding: 4 }}>
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: mfaTheme.primaryBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <Lock sx={{ fontSize: 32, color: mfaTheme.primary }} />
              </Box>
              <Typography className="form-title" variant="h5" fontWeight={600} gutterBottom sx={{ color: mfaTheme.primaryDark }}>
                Vérification MFA
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getMethodLabel()}
              </Typography>
            </Box>

            {/* Message */}
            {message && (
              <Alert severity={message.type} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

            {/* Instructions */}
            <Alert severity="info">
              {mfaMethod === '1'
                ? 'Entrez le code généré par votre application d\'authentification.'
                : 'Un code de vérification vous a été envoyé. Entrez-le ci-dessous.'}
            </Alert>

            {/* Formulaire */}
            <form onSubmit={verifyOtp}>
              <TextField
                fullWidth
                label="Code de vérification"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="Entrez le code à 6 chiffres"
                inputProps={{
                  maxLength: 8,
                  inputMode: 'numeric',
                  style: {
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    textAlign: 'center',
                    fontWeight: 600
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" />
                    </InputAdornment>
                  )
                }}
                disabled={loading}
                autoFocus
              />

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                {(mfaMethod === '2' || mfaMethod === '3') && (
                  <Button
                    variant="outlined"
                    onClick={sendChallenge}
                    disabled={sendingOtp || countdown > 0}
                    startIcon={sendingOtp ? <CircularProgress size={20} /> : <Refresh />}
                    sx={{ flex: 1 }}
                  >
                    {sendingOtp ? 'Envoi...' : countdown > 0 ? `Renvoyer (${formatCountdown(countdown)})` : 'Renvoyer'}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !otpCode.trim()}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{ 
                    flex: mfaMethod === '1' ? 1 : 2,
                    backgroundColor: mfaTheme.primary,
                    '&:hover': { backgroundColor: mfaTheme.primaryHover }
                  }}
                >
                  {loading ? 'Vérification...' : 'Vérifier'}
                </Button>
              </Stack>
            </form>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => window.location.replace('/Login')}
                startIcon={<ArrowBack />}
              >
                Retour à la connexion
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MfaVerifyPage;